import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import { Button, CircularProgress, IconButton, TextField, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as React from 'react';

const useStyles = makeStyles((theme) => ({
  root: {
    alignItems: 'center',
    display: 'flex',
  },
  button: {
    height: 'fit-content',
  },
  editIcon: {
    opacity: 0.3,
    '&:hover': {
      opacity: 1,
    },
  },
  inputRoot: {
    fontSize: '3rem',
    fontWeight: 400,
    letterSpacing: '0em',
    lineHeight: '1.167',
  },
  deleteButton: {
    color: theme.palette.getContrastText(theme.palette.warning.main),
    backgroundColor: theme.palette.warning.main,
    '&:hover': {
      backgroundColor: theme.palette.warning.dark,
    },
  },
}));

interface NameProps {
  name: string;
  onUpdate: (value: string) => Promise<any>;
  onDelete: () => void;
}

/**
 * Name display/edit
 *
 * @param {object} props - Component properties
 * @param {string} props.name     - Name
 * @param {Function} props.onUpdate - Update function
 * @returns {React.Component} - Name
 */
export const Name = ({ name, onUpdate, onDelete }: NameProps): JSX.Element => {
  const classes = useStyles();
  const [localName, setLocalName] = React.useState(name);
  const [isEditing, setIsEditing] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    setLocalName(name);
    setIsEditing(false);
  }, [name]);

  return (
    <div className={classes.root}>
      {!isEditing ? (
        <React.Fragment>
          <Typography variant="h4">{localName}</Typography>
          <IconButton onClick={() => setIsEditing(true)} className={classes.editIcon} size="large">
            <EditIcon />
          </IconButton>
          <Button
            className={classes.deleteButton}
            color="warning"
            startIcon={<DeleteForeverIcon />}
            onClick={onDelete}
          >
            Delete
          </Button>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <TextField
            fullWidth={true}
            value={localName}
            onChange={(evt) => setLocalName(evt.target.value)}
            disabled={isSaving}
            // TODO: backend OpenAPI does not enforce this length. Add `maxLength: 64`
            // to `Unit.name` in customer-lambdas/doc/webapp.yaml (mirroring units.name
            // varchar(64) in db_setup.sql), then delete this client-side cap + counter.
            // Today the DB write fails with a cryptic string_data_right_truncation
            // error if the cap is bypassed.
            inputProps={{
              className: classes.inputRoot,
              maxLength: 64,
            }}
            // TODO: backend OpenAPI does not enforce non-empty. Add `minLength: 1` to
            // `Unit.name` in customer-lambdas/doc/webapp.yaml — the DB column is NOT
            // NULL but accepts empty strings, so without this client-side guard a user
            // can save a blank unit name (silently breaking navigation labels).
            error={localName.trim() === ''}
            helperText={
              localName.trim() === '' ? 'Unit name is required' : `${localName.length}/64`
            }
            FormHelperTextProps={{ sx: { textAlign: 'right', m: 0 } }}
          />
          <IconButton
            disabled={isSaving || localName.trim() === ''}
            onClick={async () => {
              const trimmed = localName.trim();
              setIsSaving(true);
              try {
                await onUpdate(trimmed);
                setLocalName(trimmed);
                setIsEditing(false);
              } catch (err) {
                // Reset to original name on error
                setLocalName(name);
                setIsEditing(false);
              } finally {
                setIsSaving(false);
              }
            }}
            className={classes.button}
            size="large"
          >
            {isSaving ? <CircularProgress size={24} /> : <CheckIcon />}
          </IconButton>
          <IconButton
            disabled={isSaving}
            onClick={() => {
              setLocalName(name);
              setIsEditing(false);
            }}
            className={classes.button}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </React.Fragment>
      )}
    </div>
  );
};
